import React from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { FIELD_PATHS } from '@funnels/constants';
import {
  Asset,
  Expenses,
  FormValuesALIEO,
  Liability,
  PropertyAsset,
  PropertyExpenses,
  ReduxStoreData,
} from '@funnels/types';
import {
  getMarriedApplicantIds,
  getPropertyExpensesIndexesFromAssets,
  groupPropertiesByMaritalStatus,
  PropertyInGroup,
} from '@funnels/utils';

import type { PropertyExpenseTableProps } from './PropertyExpenseTable';
import PropertyExpenseTable from './PropertyExpenseTable';

function mapState(currentState: ReduxStoreData) {
  const data: Pick<FormValuesALIEO, 'applicants' | 'assets' | 'liabilities' | 'expenses'> = {
    applicants: currentState.applicants.data,
    assets: currentState.assets.data,
    liabilities: currentState.liabilities.data,
    expenses: currentState.expenses.data,
  };

  const error =
    currentState.applicants.error ||
    currentState.assets.error ||
    currentState.liabilities.error ||
    currentState.expenses.error;
  const loading =
    currentState.applicants.loading ||
    currentState.assets.loading ||
    currentState.liabilities.loading ||
    currentState.expenses.loading;

  return { data, error, loading };
}

type CommentsModalTitleFactoryType = (group: PropertyInGroup) => PropertyExpenseTableProps['commentsModalTitle'];
type CommentsModalContenFactoryType = (group: PropertyInGroup) => PropertyExpenseTableProps['commentsModalContent'];
type RenderCommentsModalFooterFactoryType = (
  group: PropertyInGroup
) => PropertyExpenseTableProps['renderCommentsModalFooter'];

type PropertyExpenseTablesProps = {
  commentsModalTitle: CommentsModalTitleFactoryType;
  commentsModalContent: CommentsModalContenFactoryType;
  renderCommentsModalFooter: RenderCommentsModalFooterFactoryType;
};

const hasComment = (applicantIds: string[]) => (expense: Expenses) =>
  applicantIds.includes(expense.applicantId!) && expense.expenses.some((e) => !!e.comments);

function PropertyExpenseTables({
  commentsModalTitle,
  commentsModalContent,
  renderCommentsModalFooter,
}: PropertyExpenseTablesProps) {
  const { data } = useSelector(mapState);
  const applicants = get(data, FIELD_PATHS.APPLICANTS(), []);
  const assets = get(data, FIELD_PATHS.ASSETS(), []) as Asset[];
  const liabilities = get(data, FIELD_PATHS.LIABILITIES(), []) as Liability[];
  const generalExpenses = get(data, FIELD_PATHS.EXPENSES(), []) as Expenses[];
  const propertyIndexes = getPropertyExpensesIndexesFromAssets({
    assets,
    liabilities,
  });
  const properties = propertyIndexes.map((asset) => {
    const property = get(data, FIELD_PATHS.ASSET(asset.assetIndex), {}) as PropertyAsset;
    const expenses = get(data, FIELD_PATHS.ASSET_PROPERTY_EXPENSES(asset.assetIndex), []) as PropertyExpenses[];
    return {
      ...property,
      // filter and correct the order of expenses
      propertyExpenses: asset.expensesIndexes.map((expenseIndex) => expenses[expenseIndex]).filter((x) => !!x),
    };
  });
  const marriedApplicantIds = getMarriedApplicantIds(applicants);

  return (
    <>
      {groupPropertiesByMaritalStatus(properties, marriedApplicantIds).map((group, index) => (
        <PropertyExpenseTable
          group={group}
          title={`Property expenses: household ${index + 1}`}
          applicants={applicants}
          hasComment={generalExpenses.some(hasComment(group.applicantIds))}
          commentsModalTitle={commentsModalTitle(group)}
          commentsModalContent={commentsModalContent(group)}
          renderCommentsModalFooter={renderCommentsModalFooter(group)}
        />
      ))}
    </>
  );
}

export default PropertyExpenseTables;
